import { voidTags } from '@common/html/util/xml';
import { SchemaPlugin } from '@common/prosemirror/model/schema-plugin';
import { nodeToDOM } from '@common/util/schema';
import { NodeSpec } from 'prosemirror-model';

export class HtmlTagsSchemaPlugin extends SchemaPlugin {
  nodes = {
    // Inline Nodes
    'abbr': this.makeInlineNode('abbr'),
    'acronym': this.makeInlineNode('acronym'),
    'applet': this.makeInlineNode('applet', {
      atom: true,
      editorTagName: 'mc-lwe-applet',
      isTagNameBox: true
    }),
    'audio': this.makeInlineNode('audio'),
    'b': this.makeInlineNode('b'),
    'basefont': this.makeLegacyInlineNode('basefont', {
      atom: true,
      isTagNameBox: true
    }),
    'bdi': this.makeInlineNode('bdi'),
    'bdo': this.makeInlineNode('bdo'),
    'bgsound': this.makeLegacyInlineNode('bgsound', {
      atom: true,
      isTagNameBox: true
    }),
    'big': this.makeInlineNode('big'),
    'blink': this.makeInlineNode('blink'),
    'canvas': this.makeInlineNode('canvas', {
      atom: true,
      editorTagName: 'mc-lwe-canvas',
      isTagNameBox: true
    }),
    'cite': this.makeInlineNode('cite'),
    'code': this.makeInlineNode('code'),
    'datalist': this.makeInlineNode('datalist', {
      atom: true,
      editorTagName: 'mc-lwe-datalist',
      isTagNameBox: true
    }),
    'del': this.makeInlineNode('del'),
    'dfn': this.makeInlineNode('dfn'),
    'dialog': this.makeInlineNode('dialog'),
    'em': this.makeInlineNode('em'),
    // embed was originally created with inline* content even though its void. It needs to stay this way for compatibility with existing docs.
    'embed': this.makeLegacyInlineNode('embed', {
      atom: true,
      editorTagName: 'mc-lwe-embed',
      isTagNameBox: true
    }),
    'font': this.makeInlineNode('font'),
    'i': this.makeInlineNode('i'),
    'ilayer': this.makeInlineNode('ilayer'),
    'ins': this.makeInlineNode('ins'),
    'isindex': this.makeInlineNode('isindex'),
    'kbd': this.makeInlineNode('kbd'),
    'keygen': this.makeLegacyInlineNode('keygen', {
      atom: true,
      isTagNameBox: true
    }),
    'layer': this.makeInlineNode('layer'),
    'legend': this.makeInlineNode('legend'),
    'mark': this.makeInlineNode('mark'),
    'multicol': this.makeInlineNode('multicol'),
    'nobr': this.makeInlineNode('nobr'),
    'noembed': this.makeInlineNode('noembed'),
    'optgroup': this.makeInlineNode('optgroup'),
    'option': this.makeInlineNode('option'),
    'output': this.makeInlineNode('output'),
    'plaintext': this.makeInlineNode('plaintext'),
    'q': this.makeInlineNode('q'), // Left off testing how the node works in the editor. Remember to check selection
    'rp': this.makeInlineNode('rp', {
      atom: true,
      editorTagName: 'mc-lwe-rp',
      isTagNameBox: true
    }),
    'rt': this.makeInlineNode('rt'),
    'ruby': this.makeInlineNode('ruby'),
    's': this.makeInlineNode('s'),
    'samp': this.makeInlineNode('samp'),
    'spacer': this.makeInlineNode('spacer'),
    'small': this.makeInlineNode('small'),
    'source': this.makeLegacyInlineNode('source', {
      atom: true,
      isTagNameBox: true
    }),
    'span': this.makeInlineNode('span'),
    'strike': this.makeInlineNode('strike'),
    'strong': this.makeInlineNode('strong'),
    'sub': this.makeInlineNode('sub'),
    'sup': this.makeInlineNode('sup'),
    'time': this.makeInlineNode('time'),
    'track': this.makeLegacyInlineNode('track', {
      atom: true,
      isTagNameBox: true
    }),
    'tt': this.makeInlineNode('tt'),
    'u': this.makeInlineNode('u'),
    'var': this.makeInlineNode('var'),
    'video': this.makeInlineNode('video'),
    'wbr': this.makeLegacyInlineNode('wbr', {
      atom: true,
      editorTagName: 'mc-lwe-wbr',
      isTagNameBox: true
    }),
    'xmp': this.makeInlineNode('xmp'),
    // Block Nodes
    'address': this.makeBlockNode('address'),
    // button was originally created with no content even though this tag can have content. It needs to stay this way for compatibility with existing docs.
    'button': {
      group: 'block',
      parseDOM: [{
        tag: 'button'
      }],
      toDOM(node) { return nodeToDOM(node); }
    },
    'center': this.makeBlockNode('center'),
    'marquee': this.makeBlockNode('marquee'),
    'meter': this.makeBlockNode('meter', { atom: true }),
    'noscript': this.makeBlockNode('noscript', {
      atom: true,
      editorTagName: 'mc-lwe-noscript',
      isTagNameBox: true
    }),
    'progress': this.makeBlockNode('progress', { atom: true })
  } satisfies Record<string, NodeSpec>;

  private makeInlineNode(tagName: string, nodeSpec?: Partial<NodeSpec>): NodeSpec {
    const isVoid = voidTags.includes(tagName);

    return {
      group: 'inline',
      content: isVoid ? undefined : 'inline*',
      inline: true,
      definingForContent: true,
      parseDOM: [{
        tag: tagName,
        preserveWhitespace: true
      }],
      toDOM(node) { return nodeToDOM(node); },
      tagName,
      isVoid,
      ...nodeSpec
    };
  }

  private makeBlockNode(tagName: string, nodeSpec?: Partial<NodeSpec>): NodeSpec {
    return {
      group: 'block',
      content: 'inline*',
      parseDOM: [{ tag: tagName }],
      toDOM(node) { return nodeToDOM(node); },
      tagName,
      ...nodeSpec
    };
  }

  private makeLegacyInlineNode(tagName: string, nodeSpec?: Partial<NodeSpec>): NodeSpec {
    return {
      group: 'inline',
      content: 'inline*',
      inline: true,
      definingForContent: true,
      parseDOM: [{
        tag: tagName,
        preserveWhitespace: true
      }],
      toDOM(node) { return nodeToDOM(node); },
      isVoid: voidTags.includes(tagName),
      ...nodeSpec
    };
  }
}
